import { useEffect, useMemo, useState } from "react";
import { TypographyNumber } from "./numbers";
import UserWidge from "@/components/modules/camera/user-widget";
import { Ellipsis } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  clearStagedUser,
  selectCurrentRoom,
  setFocusedVidExist,
  setStagedUser,
} from "@/context/features/room/roomSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import { useScreenShareActive } from "@/hooks/livekit/useScreenShareActive";
import ScreenShareView from "../modules/camera/screen-share-area";
import { MeetingTimer } from "./meet-timer";
import { RemoteParticipant } from "livekit-client";
import RoleOnly from "@/components/elements/role-only";
import { useParticipantLeave } from "@/hooks/livekit/useParticipantLeave";
import { useRemoteParticipants } from "@/hooks/livekit/useRemoteParticipants";
import { toaster } from "@/utils/toaster";
import { useIsMobile } from "@/hooks/responsive/useIsMobile";

export default function Board() {
  const [numParticipants, setNumParticipants] = useState(0);
  const [fullScreenShare, setFullScreenShare] = useState<boolean>(false);
  const { stagedUserId, roomData, focusedVid } = useSelector(selectCurrentRoom);
  const dispatch = useDispatch();
  const { livekitContext } = useLivekitContext();
  const { room } = livekitContext ?? {};
  const { t } = useTranslation();
  const hasScreen = useScreenShareActive();
  const isMobile = useIsMobile();

  const remoteParticipants = useRemoteParticipants(room);

  useParticipantLeave(room, (p) => {
    // console.log(`Participant left: ${p.identity}`);
    const found_user = remoteParticipants.find(
      (rp) => rp.identity === p.identity
    );
    if (found_user && found_user.name) {
      if (found_user.identity === focusedVid) {
        dispatch(setFocusedVidExist(false));
      }
      toaster(
        "error",
        `${found_user.name} ${t("has_left")}`,
        {
          position: isMobile ? "top-right" : "bottom-right",
          description: "",
          descriptionClassName: isMobile
            ? "font-medium dark:text-white"
            : "font-medium dark:text-white mt-2 pb-10",
          id: `${p.identity}-haslefts`,
        },
        true
      );
    }
    // console.log(found_user);
  });

  const remoteParticipantClass = useMemo(() => {
    const mapper: Record<number, string> = {
      0: "col-span-12 row-span-12",
      1: "col-span-12 row-span-12",
      2: "col-span-12 xs:col-span-6 lg:col-span-6 xl:col-span-4 row-span-4 xs:row-span-6 xl:row-span-9",
      3: "col-span-12 xs:col-span-6 lg:col-span-6 xl:col-span-3 row-span-3 xs:row-span-6 lg:row-span-6 xl:row-span-8",
      4: "col-span-6 xs:col-span-6 lg:col-span-3 xl:col-span-3 row-span-4 lg:row-span-6",
    };
    const mapperHavStaged: Record<number, string> = {
      0: "col-span-6 row-span-12",
      1: "col-span-6 row-span-12",
      2: "col-span-6 xs:col-span-6 lg:col-span-6 xl:col-span-3 row-span-6",
      3: "col-span-6 xs:col-span-6 lg:col-span-3 xl:col-span-3 row-span-3 lg:row-span-6",
      4: "col-span-6 xs:col-span-6 lg:col-span-3 xl:col-span-3 row-span-3 lg:row-span-6",
    };
    const mapperScreenShare: Record<number, string> = {
      0: "col-span-12 xl:col-span-4 row-span-4 xl:row-span-6",
      1: "col-span-6 xl:col-span-6 row-span-4 xl:row-span-6",
      2: "col-span-6 xs:col-span-4 lg:col-span-4 xl:col-span-2 row-span-4 xs:row-span-6 xl:row-span-6 ",
      3: "col-span-6 xs:col-span-6 lg:col-span-3 xl:col-span-3 row-span-3 xs:row-span-3 lg:row-span-6 xl:row-span-6 ",
      4: "col-span-4 xs:col-span-3 lg:col-span-3 xl:col-span-3 row-span-3 xl:row-span-4",
    };
    const mapperScreenShareWithHavStaged: Record<number, string> = {
      0: "col-span-12 xl:col-span-4 row-span-4 xl:row-span-6",
      1: "col-span-6 xl:col-span-6 row-span-4 xl:row-span-6",
      2: "col-span-6 md:col-span-6 lg:col-span-3 xl:col-span-2 row-span-3 md:row-span-3 lg:row-span-6 xl:row-span-6 ",
      3: "col-span-4 xs:col-span-3 lg:col-span-2 xl:col-span-2 row-span-4 xs:row-span-3 lg:row-span-6 xl:row-span-6 ",
      4: "col-span-3 xs:col-span-3 lg:col-span-3 xl:col-span-2 row-span-3 xs:row-span-3",
    };

    if (fullScreenShare) return "hidden";
    if (hasScreen && stagedUserId) {
      return (
        mapperScreenShareWithHavStaged[numParticipants] ??
        "col-span-12 row-span-12"
      );
    }
    if (hasScreen)
      return mapperScreenShare[numParticipants] ?? "col-span-12 row-span-12";
    if (stagedUserId) return mapperHavStaged[numParticipants];
    return mapper[numParticipants] ?? "p-1 w-full";
  }, [numParticipants, hasScreen, fullScreenShare, stagedUserId]);

  const localWidgetClass = useMemo(() => {
    const mapper: Record<number, string> = {
      0: "z-50 p-1 w-44 h-48 md:w-68 md:h-48 absolute top-11 start-3",
      1: remoteParticipantClass + " ",
      2: remoteParticipantClass + " ",
      3: remoteParticipantClass + " ",
      4: remoteParticipantClass,
    };
    const mapperScreenShare: Record<number, string> = {
      0: remoteParticipantClass + "",
      1: remoteParticipantClass + " ",
      2: remoteParticipantClass + " ",
      3: remoteParticipantClass + " ",
      4: remoteParticipantClass,
    };
    if (fullScreenShare) return "hidden";
    if (hasScreen)
      return mapperScreenShare[numParticipants < 2 ? 0 : numParticipants];

    return mapper[numParticipants < 2 ? 0 : numParticipants] ?? "p-1 w-full";
  }, [numParticipants, remoteParticipantClass, hasScreen, fullScreenShare]);

  const stagedWidgetClass = useMemo(() => {
    const mapper: Record<number, string> = {
      0: "col-span-12  xl:col-span-6 row-span-6 lg:row-span-12",
      1: "col-span-12 lg:col-span-9 xl:col-span-6 row-span-12 lg:row-span-12",
      2: "col-span-12 lg:col-span-6 xl:col-span-9 row-span-6 lg:row-span-12",
      3: "col-span-12 lg:col-span-6 xl:col-span-6 row-span-6 lg:row-span-12",
      4: "col-span-12 lg:col-span-6 xl:col-span-6 row-span-6 lg:row-span-12",
    };
    const mapperScreenShare: Record<number, string> = {
      0: "col-span-6 lg:col-span-3 xl:col-span-4 row-span-4 lg:row-span-4",
      1: "col-span-6 lg:col-span-2 xl:col-span-6 row-span-6 lg:row-span-6",
      2: "col-span-12 xs:col-span-6 lg:col-span-6 xl:col-span-4 row-span-4 xs:row-span-6 md:row-span-6",
      3: "col-span-12 xs:col-span-6 lg:col-span-6 row-span-4 xs:row-span-6 xs:row-span-7 md:row-span-6",
      4: "col-span-12 xs:col-span-6 lg:col-span-6 xl:col-span-6 row-span-4 xs:row-span-6",
    };
    if (fullScreenShare) return "hidden";
    if (hasScreen) return mapperScreenShare[numParticipants];
    return mapper[numParticipants];
  }, [numParticipants, hasScreen, fullScreenShare]);

  const screenShareClass = useMemo(() => {
    const defaultClass =
      "bg-black/55 border border-blue-400 shadow-sm rounded-md overflow-hidden h-full min-h-0";
    const mapper: Record<number, string> = {
      0:
        "col-span-12  xl:col-span-8 row-span-8 xl:row-span-12 max-h-[50vh] lg:max-h-[75vh] xl:max-h-[100vh] " +
        defaultClass,
      1:
        "col-span-12  xl:col-span-6 row-span-8 xl:row-span-12 max-h-[49vh] lg:max-h-[53vh] xl:max-h-[79vh] " +
        defaultClass,
      2:
        "col-span-12  xl:col-span-8 row-span-6 md:row-span-6 xl:row-span-12 max-h-[35vh] lg:max-h-[36vh] xl:max-h-[79vh] " +
        defaultClass,
      3:
        "col-span-12  xl:col-span-6 row-span-6 xs:row-span-6 xl:row-span-12 lg:row-span-6 max-h-[32vh]  xs:max-h-[39vh] md:max-h-[40vh] md:max-h-[35vh] lg:max-h-[36vh] xl:max-h-[77vh] " +
        defaultClass,
      4:
        "col-span-12 lg:col-span-6 xl:col-span-6 row-span-6 lg:row-span-12  max-h-[34vh] xs:max-h-[35vh]  lg:max-h-[77vh] xl:max-h-[77vh] 2xl:max-h-[80vh]" +
        defaultClass,
    };

    if (fullScreenShare)
      return (
        "col-span-12 row-span-12 max-h-[80vh] md:max-h-[80vh] lg:max-h-[77vh] xl:max-h-[80vh] 2xl:max-h-[80vh]" +
        defaultClass
      );
    return mapper[numParticipants];
  }, [numParticipants, fullScreenShare]);

  useEffect(() => {
    if (!room) return;

    const emptyStage = remoteParticipants.find(
      (item: RemoteParticipant) => item.identity == stagedUserId
    );

    if (emptyStage) dispatch(clearStagedUser());

    const updateParticipants = () =>
      setNumParticipants(room.remoteParticipants.size);
    updateParticipants();
    room.on("participantConnected", updateParticipants);
    room.on("participantDisconnected", updateParticipants);

    return () => {
      room.off("participantConnected", updateParticipants);
      room.off("participantDisconnected", updateParticipants);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, dispatch]);

  useEffect(() => {
    if (numParticipants < 2) {
      dispatch(setStagedUser(""));
    }
    if (remoteParticipants.find((item) => item.identity === focusedVid))
      dispatch(setFocusedVidExist(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numParticipants, dispatch, remoteParticipants]);

  return room ? (
    <div className="grow flex flex-col gap-2 relative p-2 bg-chat-background">
      {/* Participant count */}
      <div className="w-full flex justify-between">
        <div className="flex gap-1 ">
          <span className="text-primary/80 font-medium">
            {t("num_of_online")} :
          </span>
          <span className="text-primary font-medium">
            <TypographyNumber value={numParticipants + 1} className="mx-1" />
          </span>
          {/* <span className="p-1 bg-accent">{stagedUserId}</span> */}
        </div>
        <div>
          <RoleOnly extraCondition={!!roomData?.startTime}>
            <MeetingTimer startTime={roomData!.startTime} />
          </RoleOnly>
        </div>
      </div>

      <div className={"grow grid grid-cols-12 grid-rows-12 gap-2"}>
        {hasScreen && (
          <div className={screenShareClass}>
            <ScreenShareView
              isFullScreen={fullScreenShare}
              fullScreenToggle={() => setFullScreenShare((prev) => !prev)}
            />
          </div>
        )}
        {stagedUserId && room.localParticipant.identity === stagedUserId && (
          <div
            key={room.localParticipant.identity}
            className={stagedWidgetClass}
          >
            <div className="h-full w-full rounded-lg border border-input flex flex-col justify-between items-center relative">
              <UserWidge
                isAdmin={
                  livekitContext?.roomData?.adminId ===
                  room.localParticipant.identity
                }
                user={room.localParticipant}
                roomLength={numParticipants + 1}
                isLocal={true}
              />
            </div>
          </div>
        )}
        {stagedUserId &&
          remoteParticipants.map(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (participant: any) =>
              participant.identity === stagedUserId && (
                <div key={participant.identity} className={stagedWidgetClass}>
                  <div className="h-full w-full rounded-lg border border-input flex flex-col justify-between items-center relative">
                    <UserWidge
                      user={participant}
                      roomLength={numParticipants + 1}
                      isLocal={false}
                      isAdmin={
                        livekitContext?.roomData?.adminId ===
                        room.localParticipant.identity
                      }
                    />
                  </div>
                </div>
              )
          )}
        {/* Local user */}
        {room.localParticipant.identity !== stagedUserId && (
          <div
            key={room.localParticipant.identity}
            className={localWidgetClass}
          >
            <div className="h-full w-full rounded-lg  border border-input flex flex-col justify-between items-center relative ">
              <UserWidge
                isAdmin={
                  livekitContext?.roomData?.adminId ===
                  room.localParticipant.identity
                }
                user={room.localParticipant}
                roomLength={numParticipants + 1}
                isLocal={true}
              />
            </div>
          </div>
        )}
        {/* Remote participants */}
        {remoteParticipants.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (participant: any) =>
            participant.identity !== stagedUserId && (
              <div
                key={participant.identity}
                className={remoteParticipantClass}
              >
                <div className="h-full w-full rounded-lg  border border-input flex flex-col justify-between items-center relative">
                  <UserWidge
                    user={participant}
                    roomLength={numParticipants + 1}
                    isLocal={false}
                    isAdmin={
                      livekitContext?.roomData?.adminId ===
                      room.localParticipant.identity
                    }
                  />
                </div>
              </div>
            )
        )}
        {/* Overflow Indicator */}
        {numParticipants + 1 > 5 && (
          <div className="col-span-3 rounded-lg shadow border border-gray-200 flex justify-center items-center">
            <span className="text-gray-400 w-10 h-10 flex items-center justify-center bg-gray-600/25 rounded-full cursor-pointer hover:shadow">
              <Ellipsis className="text-white" />
            </span>
          </div>
        )}
      </div>
    </div>
  ) : (
    <div></div>
  );
}
